import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { formatCurrency } from "../Obras/obraFormat";
import {
  getRentArrears,
  getRentRevenueByProperty,
  type RentArrearsReport,
  type RentRevenueReport,
} from "../../relatorios/relatoriosApi";

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

type Tab = "receita" | "incobrabilidade";

const TABS: { id: Tab; label: string }[] = [
  { id: "receita", label: "Receita por imóvel" },
  { id: "incobrabilidade", label: "Incobrabilidade" },
];

export function RelatoriosView() {
  const currentYear = new Date().getFullYear();

  const [tab, setTab] = useState<Tab>("receita");
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState<number>(0); // 0 = ano inteiro

  const [revenue, setRevenue] = useState<RentRevenueReport | null>(null);
  const [arrears, setArrears] = useState<RentArrearsReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const monthArg = month > 0 ? month : undefined;
    try {
      const [rev, arr] = await Promise.all([
        getRentRevenueByProperty(year, monthArg),
        getRentArrears(year, monthArg),
      ]);
      setRevenue(rev);
      setArrears(arr);
    } catch (err) {
      setError(errorMessage(err, "Não foi possível carregar os relatórios."));
    } finally {
      setIsLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y += 1) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Relatórios"
        subtitle="Receita e incobrabilidade de rendas por imóvel e período"
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {error && (
          <div
            className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {error}
          </div>
        )}

        {/* Período (ano / mês) */}
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Ano
            <select
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-secondary-text">
            Mês
            <select
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
              className="px-3 py-2 border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              <option value={0}>Ano inteiro</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Separadores */}
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map((entry) => {
            const isActive = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => setTab(entry.id)}
                className={`inline-flex items-center px-3 py-1 text-sm cursor-pointer ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-accent/50"
                }`}
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {entry.label}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <p className="text-sm text-secondary-text py-6">A carregar…</p>
        ) : tab === "receita" ? (
          <ReceitaSection revenue={revenue} />
        ) : (
          <IncobrabilidadeSection arrears={arrears} />
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "default" | "positive" | "negative";
}) {
  const valueClass =
    tone === "positive"
      ? "text-green-700"
      : tone === "negative"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div
      className="bg-card border border-border p-4"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <p
        className="text-secondary-text"
        style={{ fontSize: "var(--text-caption)" }}
      >
        {label}
      </p>
      <p
        className={`mt-1 ${valueClass}`}
        style={{
          fontSize: "var(--text-h4)",
          fontWeight: "var(--font-weight-semi-bold)",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function ReceitaSection({ revenue }: { revenue: RentRevenueReport | null }) {
  if (!revenue || revenue.rows.length === 0) {
    return (
      <p className="text-sm text-secondary-text py-6">
        Sem receita de rendas para o período.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          label="Receita esperada"
          value={formatCurrency(revenue.totalExpected)}
        />
        <StatCard
          label="Recebido"
          value={formatCurrency(revenue.totalReceived)}
          tone="positive"
        />
        <StatCard
          label="Por receber"
          value={formatCurrency(revenue.totalPending)}
          tone={revenue.totalPending > 0 ? "negative" : "default"}
        />
      </div>

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 720 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3">Imóvel</th>
              <th className="px-4 py-3 text-right" style={{ width: 90 }}>
                Avisos
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Esperado
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Recebido
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Por receber
              </th>
            </tr>
          </thead>
          <tbody>
            {revenue.rows.map((row) => (
              <tr
                key={row.propertyId}
                className="border-b border-border last:border-b-0 hover:bg-accent/30"
              >
                <td
                  className="px-4 py-3 break-words"
                  style={{ fontWeight: "var(--font-weight-medium)" }}
                >
                  {row.propertyName}
                </td>
                <td className="px-4 py-3 text-right">{row.noticeCount}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {formatCurrency(row.expectedTotal)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-green-700">
                  {formatCurrency(row.receivedTotal)}
                </td>
                <td
                  className={`px-4 py-3 text-right whitespace-nowrap ${
                    row.pendingTotal > 0 ? "text-destructive" : ""
                  }`}
                >
                  {formatCurrency(row.pendingTotal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IncobrabilidadeSection({
  arrears,
}: {
  arrears: RentArrearsReport | null;
}) {
  if (!arrears || arrears.overdueCount === 0) {
    return (
      <p className="text-sm text-secondary-text py-6">
        Sem rendas em atraso para o período.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard
          label="Total em atraso"
          value={formatCurrency(arrears.totalOverdue)}
          tone="negative"
        />
        <StatCard
          label="Rendas em atraso"
          value={String(arrears.overdueCount)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por imóvel */}
        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 360 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3">Imóvel</th>
                <th className="px-4 py-3 text-right" style={{ width: 80 }}>
                  Nº
                </th>
                <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                  Em atraso
                </th>
              </tr>
            </thead>
            <tbody>
              {arrears.byProperty.map((row) => (
                <tr
                  key={row.propertyId}
                  className="border-b border-border last:border-b-0 hover:bg-accent/30"
                >
                  <td
                    className="px-4 py-3 break-words"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {row.propertyName}
                  </td>
                  <td className="px-4 py-3 text-right">{row.overdueCount}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-destructive">
                    {formatCurrency(row.overdueTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Por inquilino */}
        <div
          className="bg-card border border-border overflow-x-auto"
          style={{ borderRadius: "var(--radius-card)" }}
        >
          <table className="w-full" style={{ minWidth: 360 }}>
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-4 py-3">Inquilino</th>
                <th className="px-4 py-3 text-right" style={{ width: 80 }}>
                  Nº
                </th>
                <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                  Em atraso
                </th>
              </tr>
            </thead>
            <tbody>
              {arrears.byTenant.map((row) => (
                <tr
                  key={row.tenantId}
                  className="border-b border-border last:border-b-0 hover:bg-accent/30"
                >
                  <td
                    className="px-4 py-3 break-words"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {row.tenantName}
                  </td>
                  <td className="px-4 py-3 text-right">{row.overdueCount}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-destructive">
                    {formatCurrency(row.overdueTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
